import { css } from '@emotion/react';
import { FunctionComponent, SVGProps, useContext } from 'react';
import { useSelector } from 'react-redux';
import { Button } from 'react-bootstrap';
import t from 'react-translate';
import { config } from '@config/pendo.config.json';
import NvIcon from 'shared/components/nv-icon';
import NvPopover from 'shared/components/nv-popover';
import NvTooltip from 'shared/components/nv-tooltip';
import MentoringProgramContext from 'athena/components/mentoring-program/context';
import { getMentoringProgramAliases } from 'redux/selectors/mentoring-programs';
import { gray5 } from 'styles/global_defaults/colors';
import { mobile } from 'styles/global_defaults/media-queries';
import { quarterSpacing, threeQuartersSpacing } from 'styles/global_defaults/scaffolding';
import { ReactComponent as ProgramEngagementReportingGraphic } from 'styles/icons/program-engagement-reporting.svg';
import { ReactComponent as SessionPulseChecksReportingGraphic } from 'styles/icons/session-pulse-checks-reporting.svg';
import { ReactComponent as DevelopmentGoalsReportingGraphic } from 'styles/icons/development-goals-reporting.svg';
import { ReactComponent as ParticipantProgramProfilesReportingGraphic } from 'styles/icons/participant-program-profiles-reporting.svg';


const ProgramEngagementPopover = ({ aliases }) => (
  <div>
    <div className='gray-3 font-weight-bold mb-2'>
      {t.MENTORING_PROGRAMS.REPORTING.DOWNLOAD_INCLUDES()}
    </div>
    <ul className='pl-4 mb-0'>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PROGRAM_ENGAGEMENT_POPOVER.MENTOR_NAME(aliases)}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PROGRAM_ENGAGEMENT_POPOVER.MENTEE_NAME(aliases)}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PROGRAM_ENGAGEMENT_POPOVER.MATCHED_DATE()}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PROGRAM_ENGAGEMENT_POPOVER.MEETING_PLAN_CADENCE()}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PROGRAM_ENGAGEMENT_POPOVER.MEETING_PLAN_ENDS_DATE()}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PROGRAM_ENGAGEMENT_POPOVER.MEETING_PLAN_DURATION()}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PROGRAM_ENGAGEMENT_POPOVER.SESSIONS_TOTAL()}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PROGRAM_ENGAGEMENT_POPOVER.SESSIONS_PAST()}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PROGRAM_ENGAGEMENT_POPOVER.SESSIONS_UPCOMING()}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PROGRAM_ENGAGEMENT_POPOVER.ACTION_ITEMS_TOTAL()}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PROGRAM_ENGAGEMENT_POPOVER.ACTION_ITEMS_OUTSTANDING()}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PROGRAM_ENGAGEMENT_POPOVER.ACTION_ITEMS_COMPLETE()}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PROGRAM_ENGAGEMENT_POPOVER.ACTION_ITEMS_TOTAL_MENTOR_OWNED(aliases)}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PROGRAM_ENGAGEMENT_POPOVER.ACTION_ITEMS_TOTAL_MENTEE_OWNED(aliases)}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PROGRAM_ENGAGEMENT_POPOVER.MENTOR_LAST_VISIT(aliases)}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PROGRAM_ENGAGEMENT_POPOVER.MENTEE_LAST_VISIT(aliases)}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PROGRAM_ENGAGEMENT_POPOVER.LAST_SESSION_DATE()}</li>
    </ul>
  </div>
);

const PulseChecksPopover = ({ aliases }) => (
  <div>
    <div className='gray-3 font-weight-bold mb-2'>
      {t.MENTORING_PROGRAMS.REPORTING.DOWNLOAD_INCLUDES()}
    </div>
    <ul className='pl-4 mb-0'>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PULSE_CHECKS_POPOVER.MENTOR_NAME(aliases)}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PULSE_CHECKS_POPOVER.MENTEE_NAME(aliases)}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PULSE_CHECKS_POPOVER.SESSION_DATE()}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PULSE_CHECKS_POPOVER.MENTEE_RESPONSE(aliases)}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PULSE_CHECKS_POPOVER.MENTOR_RESPONSE(aliases)}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PULSE_CHECKS_POPOVER.MENTEE_RESPONSE_DATE(aliases)}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PULSE_CHECKS_POPOVER.MENTOR_RESPONSE_DATE(aliases)}</li>
    </ul>
  </div>
);

const DevelopmentGoalsPopover = () => (
  <div>
    <div className='gray-3 font-weight-bold mb-2'>
      {t.MENTORING_PROGRAMS.REPORTING.DOWNLOAD_INCLUDES()}
    </div>
    <ul className='pl-4 mb-0'>
      <li>{t.MENTORING_PROGRAMS.REPORTING.DEVELOPMENT_GOALS_POPOVER.USERS_EMAIL()}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.DEVELOPMENT_GOALS_POPOVER.EXTERNAL_ID()}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.DEVELOPMENT_GOALS_POPOVER.FIRST_NAME()}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.DEVELOPMENT_GOALS_POPOVER.LAST_NAME()}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.DEVELOPMENT_GOALS_POPOVER.GOAL_CREATION_DATE()}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.DEVELOPMENT_GOALS_POPOVER.GOAL()}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.DEVELOPMENT_GOALS_POPOVER.STATUS()}</li>
    </ul>
  </div>
);

const ParticipantProgramProfilesPopover = () => (
  <div>
    <div className='gray-3 font-weight-bold mb-2'>
      {t.MENTORING_PROGRAMS.REPORTING.DOWNLOAD_INCLUDES()}
    </div>
    <ul className='pl-4 mb-0'>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PARTICIPANT_PROGRAM_PROFILES_POPOVER.USERS_EMAIL()}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PARTICIPANT_PROGRAM_PROFILES_POPOVER.EXTERNAL_ID()}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PARTICIPANT_PROGRAM_PROFILES_POPOVER.FIRST_NAME()}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PARTICIPANT_PROGRAM_PROFILES_POPOVER.LAST_NAME()}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PARTICIPANT_PROGRAM_PROFILES_POPOVER.HEADLINE()}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PARTICIPANT_PROGRAM_PROFILES_POPOVER.BIO()}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PARTICIPANT_PROGRAM_PROFILES_POPOVER.CITY()}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PARTICIPANT_PROGRAM_PROFILES_POPOVER.STATE()}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PARTICIPANT_PROGRAM_PROFILES_POPOVER.COUNTRY()}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PARTICIPANT_PROGRAM_PROFILES_POPOVER.ORG_LEVEL_QUESTIONS()}</li>
      <li>{t.MENTORING_PROGRAMS.REPORTING.PARTICIPANT_PROGRAM_PROFILES_POPOVER.PROGRAM_LEVEL_QUESTIONS()}</li>
    </ul>
  </div>
);

const Reporting = () => {
  const styles = css`
    .download-grid {
      display: grid;
      grid-template-columns: 1fr 1fr;
      gap: ${threeQuartersSpacing}px;

      ${mobile(css`
        grid-template-columns: 1fr;
      `)}
    }
  `;

  const { mentoringProgram } = useContext(MentoringProgramContext);
  const mentoringProgramAliases = useSelector((state) => getMentoringProgramAliases(state, mentoringProgram?.id));

  return (
    <div css={styles}>
      <div className='heading-3 mb-4'>
        {t.MENTORING_PROGRAMS.REPORTING.DATA_DOWNLOADS()}
        <NvTooltip text={t.MENTORING_PROGRAMS.REPORTING.DATA_DOWNLOADS_TOOLTIP()} placement='top'>
          <NvIcon icon='info' size='smallest' className='d-inline gray-3 ml-1' />
        </NvTooltip>
      </div>
      <div className='download-grid'>
        <DataDownloadCard
          title={t.MENTORING_PROGRAMS.REPORTING.PROGRAM_ENGAGEMENT()}
          description={t.MENTORING_PROGRAMS.REPORTING.PROGRAM_ENGAGEMENT_DESCRIPTION()}
          downloadUrl={`/mentorship_programs/${mentoringProgram?.id}/download_report?report_type=program_engagement`}
          ariaLabel={t.MENTORING_PROGRAMS.REPORTING.PROGRAM_ENGAGEMENT_ARIA_LABEL()}
          dataQa={config.pendo.athena.mentorshipProgram.reporting.downloadProgramEngagementReport}
          Graphic={() => <ProgramEngagementReportingGraphic height={75} width={110} />}
          PopoverContent={() => <ProgramEngagementPopover aliases={mentoringProgramAliases} />}
        />
        <DataDownloadCard
          title={t.MENTORING_PROGRAMS.REPORTING.PULSE_CHECKS()}
          description={t.MENTORING_PROGRAMS.REPORTING.PULSE_CHECKS_DESCRIPTION()}
          downloadUrl={`/mentorship_programs/${mentoringProgram?.id}/download_report?report_type=pulse_check`}
          ariaLabel={t.MENTORING_PROGRAMS.REPORTING.PULSE_CHECKS_ARIA_LABEL()}
          dataQa={config.pendo.athena.mentorshipProgram.reporting.downloadPulseChecksReport}
          Graphic={() => <SessionPulseChecksReportingGraphic height={80} width={110} />}
          PopoverContent={() => <PulseChecksPopover aliases={mentoringProgramAliases} />}
        />
        <DataDownloadCard
          title={t.MENTORING_PROGRAMS.REPORTING.DEVELEOPMENT_GOALS()}
          description={t.MENTORING_PROGRAMS.REPORTING.DEVELEOPMENT_GOALS_DESCRIPTION(mentoringProgramAliases)}
          downloadUrl={`/mentorship_programs/${mentoringProgram?.id}/download_report?report_type=goals`}
          ariaLabel={t.MENTORING_PROGRAMS.REPORTING.DEVELEOPMENT_GOALS_ARIA_LABEL()}
          dataQa={config.pendo.athena.mentorshipProgram.reporting.downloadDevelopmentGoalsReport}
          Graphic={() => <DevelopmentGoalsReportingGraphic height={75} width={100} />}
          PopoverContent={() => <DevelopmentGoalsPopover />}
        />
        <DataDownloadCard
          title={t.MENTORING_PROGRAMS.REPORTING.PARTICIPANT_PROGRAM_PROFILES()}
          description={t.MENTORING_PROGRAMS.REPORTING.PARTICIPANT_PROGRAM_PROFILES_DESCRIPTION(mentoringProgramAliases)}
          downloadUrl={`/mentorship_programs/${mentoringProgram?.id}/download_report?report_type=program_profile`}
          ariaLabel={t.MENTORING_PROGRAMS.REPORTING.PARTICIPANT_PROGRAM_PROFILES_ARIA_LABEL()}
          dataQa={config.pendo.athena.mentorshipProgram.reporting.downloadParticipantProgramProfilesReport}
          Graphic={() => <ParticipantProgramProfilesReportingGraphic height={85} width={100} />}
          PopoverContent={() => <ParticipantProgramProfilesPopover />}
        />
      </div>
    </div>
  );
};

export default Reporting;

type DataDownloadCardProps = {
  title: string;
  description: string;
  downloadUrl: string;
  ariaLabel: string;
  dataQa: string;
  Graphic: FunctionComponent<SVGProps<SVGSVGElement>>;
  PopoverContent: FunctionComponent;
};

const DataDownloadCard = ({
  title,
  description,
  downloadUrl,
  ariaLabel,
  dataQa,
  Graphic,
  PopoverContent,
}: DataDownloadCardProps) => {
  const styles = css`
    border-radius: ${quarterSpacing}px;
    border: 1px solid ${gray5};

    .svg-container {
      height: fit-content;
    }
  `;

  return (
    <div css={styles} className='download-card d-flex flex-column p-2'>
      <div className='d-flex justify-content-between align-items-center m-2'>
        <div className='font-weight-bold mr-4'>{title}</div>
        <Button
          variant='secondary'
          className='d-flex align-items-center'
          href={downloadUrl}
          target='_blank'
          aria-label={ariaLabel}
          data-qa={dataQa}
        >
          <NvIcon icon='download' size='smallest' className='mr-1' />
          {t.MENTORING_PROGRAMS.REPORTING.DOWNLOAD()}
        </Button>
      </div>
      <div className='d-flex flex-grow-1 justify-content-between'>
        <div className='d-flex flex-column justify-content-between m-2'>
          <div className='text-body gray-2'>{description}</div>
          <div className='d-flex font-weight-bold text-small mt-4'>
            {t.MENTORING_PROGRAMS.REPORTING.WHATS_INCLUDED()}
            <NvPopover
              showOnHover
              placement='top'
              content={<PopoverContent />}
            >
              <NvIcon icon='info' size='smallest' className='gray-3 ml-1' />
            </NvPopover>
          </div>
        </div>
        <div className='svg-container my-auto'>
          <Graphic />
        </div>
      </div>
    </div>
  );
};
